import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { NEWS_CATEGORY_LABEL, news } from "@/mocks/news";
import NewsListItem from "@/components/feature/NewsListItem";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageContainer from "@/components/base/PageContainer";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = news.find((n) => n.id === id);
  const [showSuggestion, setShowSuggestion] = useState(false);

  usePageMeta(
    item ? `${item.title} - 影视影院资讯详情` : "资讯详情 - 影视影院",
    item ? `${item.summary.slice(0, 80)}` : "影视影院资讯详情页",
    item ? `影视影院,${NEWS_CATEGORY_LABEL[item.category]},${item.tags.join(",")},影视资讯` : "影视资讯"
  );

  const related = useMemo(() => {
    if (!item) return [];
    const sameCategory = news.filter((n) => n.id !== item.id && n.category === item.category);
    const others = news.filter((n) => n.id !== item.id && n.category !== item.category);
    return [...sameCategory, ...others].slice(0, 3);
  }, [item]);

  useEffect(() => {
    setShowSuggestion(false);
    const onScroll = () => {
      const reached =
        window.innerHeight + window.scrollY >= document.body.scrollHeight - 240;
      if (reached) setShowSuggestion(true);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [id]);

  if (!item) {
    return (
      <PageContainer className="py-20">
        <div className="flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
            <i className="ri-article-line text-3xl" />
          </span>
          <h1 className="font-heading font-extrabold text-xl text-foreground-900">未找到该资讯</h1>
          <p className="mt-2 text-sm text-foreground-500">这篇资讯可能已被删除，去资讯列表看看吧。</p>
          <Link
            to="/news"
            className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-article-line" />
            返回资讯列表
          </Link>
        </div>
      </PageContainer>
    );
  }

  return (
    <div className="w-full bg-background-100">
      <PageContainer className="py-6 md:py-8">
        <Breadcrumb items={[{ label: "影视资讯", to: "/news" }, { label: item.title }]} />

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <article className="lg:col-span-2 rounded-lg bg-background-50 border border-background-200 overflow-hidden">
            <div className="p-5 md:p-7 pb-0">
              <Link
                to={`/news?category=${item.category}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-label hover:bg-accent-200 transition-colors cursor-pointer"
              >
                <i className="ri-price-tag-3-line" />
                {NEWS_CATEGORY_LABEL[item.category]}
              </Link>
              <h1 className="mt-3 font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 leading-snug">
                {item.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-user-3-line" />
                  {item.author}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {item.date}
                </span>
              </div>
            </div>

            <div className="p-5 md:p-7">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} 配图`}
                  title={`${item.title} - 影视影院资讯详情`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-6 flex flex-col gap-4">
                {item.content.map((paragraph, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-background-200">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-accent-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  返回资讯列表
                </Link>
              </div>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-20">
              <SectionHeading title="相关文章" icon="ri-links-line" />
              <div className="flex flex-col gap-3">
                {related.map((rel) => (
                  <NewsListItem key={rel.id} item={rel} />
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* 阅读完毕建议 */}
        {related.length > 0 && (
          <div
            className={`mt-8 rounded-lg bg-primary-950 text-background-100 p-5 md:p-6 transition-all duration-500 ${
              showSuggestion ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
            }`}
          >
            <div className="flex items-center gap-2">
              <i className="ri-bookmark-3-line text-xl text-accent-400" />
              <h3 className="font-heading font-bold text-white">读完了？继续看看这些</h3>
            </div>
            <p className="mt-1.5 text-sm text-background-300">
              根据本篇资讯的主题，为你挑选了以下内容。
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {related.map((rel) => (
                <Link
                  key={`suggest-${rel.id}`}
                  to={`/news/detail/${rel.id}`}
                  className="group rounded-md bg-primary-900 hover:bg-primary-800 p-3 transition-colors cursor-pointer"
                >
                  <p className="text-sm text-background-100 leading-snug line-clamp-2 group-hover:text-accent-300 transition-colors">
                    {rel.title}
                  </p>
                  <p className="mt-2 text-xs text-background-400">{rel.date}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <p className="mt-8 text-xs text-foreground-400 text-center leading-relaxed">
          本篇资讯为虚构演示内容，作者、日期与事件均为演示数据。
        </p>
      </PageContainer>
    </div>
  );
}